const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const USERNAME_PATTERN = /^[a-zA-Z0-9_]+$/;
// Backend signUpUser only provisions creator/customer accounts (admin is rejected),
// so the bulk path accepts the same set.
const VALID_ROLES = new Set(["creator", "customer"]);

// Must match the backend MAX_BULK_CREATOR_ROWS limit.
export const MAX_BULK_SIGNUP_ROWS = 200;

export const BULK_SIGNUP_FORMAT_HINT =
    "Required columns: Username, Role, Email, and Full Name (or First Name + Last Name). Optional: Insta URL.";

const COLUMN_ALIASES = {
    email: new Set(["email", "e-mail"]),
    username: new Set(["username", "user name", "reserved username", "reserved_username"]),
    role: new Set(["role"]),
    fullname: new Set(["full name", "fullname", "name", "artiste", "artist"]),
    firstname: new Set(["first name", "firstname", "first_name"]),
    lastname: new Set(["last name", "lastname", "last_name", "surname"]),
    instaurl: new Set(["insta url", "instaurl", "instagram", "instagram url", "insta"]),
};

function normalizeCell(value) {
    if (value == null) return "";
    return String(value).trim();
}

function normalizeHeader(value) {
    return normalizeCell(value)
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
}

function isValidEmail(email) {
    return EMAIL_PATTERN.test(email);
}

function sanitizeNamePart(value) {
    const s = normalizeCell(value);
    if (!s || /^null$/i.test(s)) return "";
    return s;
}

function splitFullName(fullName) {
    const s = sanitizeNamePart(fullName);
    if (!s) return { firstName: "", lastName: "" };
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function detectDelimiter(headerLine) {
    const tabs = (headerLine.match(/\t/g) || []).length;
    const commas = (headerLine.match(/,/g) || []).length;
    return tabs > commas ? "\t" : ",";
}

function splitDelimitedLine(line, delimiter = ",") {
    const cells = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }
        const isSeparator =
            !inQuotes &&
            (char === delimiter || (delimiter === "," && (char === ";" || char === "\t")));
        if (isSeparator) {
            cells.push(current.trim());
            current = "";
            continue;
        }
        current += char;
    }

    cells.push(current.trim());
    return cells;
}

function findColumnIndex(headers, key) {
    const aliases = COLUMN_ALIASES[key];
    return headers.findIndex((h) => aliases.has(normalizeHeader(h)));
}

function validateHeaders(headers) {
    const normalized = headers.map(normalizeHeader);
    const hasEmail = normalized.some((h) => COLUMN_ALIASES.email.has(h));
    const hasUsername = normalized.some((h) => COLUMN_ALIASES.username.has(h));
    const hasRole = normalized.some((h) => COLUMN_ALIASES.role.has(h));
    const hasFullName = normalized.some((h) => COLUMN_ALIASES.fullname.has(h));
    const hasFirstName = normalized.some((h) => COLUMN_ALIASES.firstname.has(h));
    const hasLastName = normalized.some((h) => COLUMN_ALIASES.lastname.has(h));
    const hasNameColumns = hasFullName || (hasFirstName && hasLastName);

    if (hasEmail && hasUsername && hasRole && hasNameColumns) {
        return { ok: true };
    }

    const missing = [];
    if (!hasEmail) missing.push("Email");
    if (!hasUsername) missing.push("Username");
    if (!hasRole) missing.push("Role");
    if (!hasNameColumns) missing.push("Full Name (or First Name + Last Name)");

    return {
        ok: false,
        message: `${BULK_SIGNUP_FORMAT_HINT} Missing: ${missing.join(", ")}.`,
    };
}

/**
 * @param {Record<string, string>} fields
 * @param {number} rowNumber
 */
function buildEntry(fields, rowNumber) {
    const email = normalizeCell(fields.email).toLowerCase();
    const username = normalizeCell(fields.username);
    const role = normalizeCell(fields.role).toLowerCase();
    let firstName = sanitizeNamePart(fields.firstName);
    let lastName = sanitizeNamePart(fields.lastName);

    const fullnameRaw = fields.fullname;
    if (fullnameRaw) {
        const split = splitFullName(fullnameRaw);
        if (!firstName) firstName = split.firstName;
        if (!lastName) lastName = split.lastName;
    }

    const instaUrl = normalizeCell(fields.instaUrl);

    if (!username) {
        return { ok: false, rowNumber, reason: "Username is required." };
    }
    if (!USERNAME_PATTERN.test(username)) {
        return { ok: false, rowNumber, reason: "Username can only contain letters, numbers and underscores." };
    }
    if (!role) {
        return { ok: false, rowNumber, reason: "Role is required." };
    }
    if (!VALID_ROLES.has(role)) {
        return { ok: false, rowNumber, reason: "Role must be creator or customer." };
    }
    if (!email) {
        return { ok: false, rowNumber, reason: "Email is required." };
    }
    if (!isValidEmail(email)) {
        return { ok: false, rowNumber, reason: "Invalid email format." };
    }
    if (!firstName && !lastName) {
        return { ok: false, rowNumber, reason: "Full name or first and last name is required." };
    }

    const entry = {
        email,
        username,
        role,
        ...(firstName && lastName
            ? { firstName, lastName }
            : firstName
              ? { fullname: firstName }
              : { fullname: lastName }),
        ...(instaUrl ? { instaUrl } : {}),
    };

    return { ok: true, entry };
}

/**
 * @param {string[]} headers
 * @param {string[]} cells
 * @param {number} rowNumber
 */
function parseHeaderRow(headers, cells, rowNumber) {
    const emailIdx = findColumnIndex(headers, "email");
    const usernameIdx = findColumnIndex(headers, "username");
    const roleIdx = findColumnIndex(headers, "role");
    const fullnameIdx = findColumnIndex(headers, "fullname");
    const firstnameIdx = findColumnIndex(headers, "firstname");
    const lastnameIdx = findColumnIndex(headers, "lastname");
    const instaIdx = findColumnIndex(headers, "instaurl");

    const get = (idx) => (idx >= 0 && cells[idx] != null ? normalizeCell(cells[idx]) : "");

    const fullNameRaw = fullnameIdx >= 0 ? get(fullnameIdx) : "";
    const fromFull = splitFullName(fullNameRaw);
    const firstName = firstnameIdx >= 0 ? sanitizeNamePart(get(firstnameIdx)) : fromFull.firstName;
    const lastName = lastnameIdx >= 0 ? sanitizeNamePart(get(lastnameIdx)) : fromFull.lastName;

    return buildEntry(
        {
            email: get(emailIdx),
            username: get(usernameIdx),
            role: get(roleIdx),
            firstName,
            lastName,
            fullname: fullNameRaw,
            instaUrl: instaIdx >= 0 ? get(instaIdx) : "",
        },
        rowNumber,
    );
}

/**
 * @param {string} text
 * @returns {{ ok: boolean, message?: string, entries: object[], skipped: { rowNumber: number, reason: string }[] }}
 */
export function parseBulkSignupInput(text) {
    const trimmed = text?.trim();
    if (!trimmed) {
        return { ok: false, message: "Input is empty.", entries: [], skipped: [] };
    }

    const lines = trimmed.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length === 0) {
        return { ok: false, message: "Input is empty.", entries: [], skipped: [] };
    }

    // A header row is required. Every column — including Role — is mapped by its
    // header name, never guessed from cell values, so a name that happens to equal
    // a role word ("Creator", "Admin") can never be mistaken for the role column.
    const delimiter = detectDelimiter(lines[0]);
    const headerCells = splitDelimitedLine(lines[0], delimiter);

    const headerCheck = validateHeaders(headerCells);
    if (!headerCheck.ok) {
        return { ok: false, message: headerCheck.message, entries: [], skipped: [] };
    }

    const dataLines = lines.slice(1);
    if (dataLines.length === 0) {
        return { ok: false, message: "Add at least one data row below the header.", entries: [], skipped: [] };
    }

    const entries = [];
    const skipped = [];
    const seenEmails = new Set();

    dataLines.forEach((line, index) => {
        const rowNumber = index + 2;
        const cells = splitDelimitedLine(line, delimiter);
        const parsed = parseHeaderRow(headerCells, cells, rowNumber);

        if (!parsed.ok) {
            skipped.push({ rowNumber, reason: parsed.reason });
            return;
        }

        if (seenEmails.has(parsed.entry.email)) {
            skipped.push({ rowNumber, reason: "Duplicate email in batch." });
            return;
        }

        seenEmails.add(parsed.entry.email);
        entries.push(parsed.entry);
    });

    return { ok: true, entries, skipped };
}
